import { useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSendOTP } from '../../hooks/useSendOTP';
import { useMutation } from '@tanstack/react-query';
import { OTPInput } from '../resetPassword/OTPInput';
import { verifyEmail } from '../../api/authAPI';
import { resendVerifyEmailOTP as resendVerifyEmailAPI } from '../../api/authAPI';

import useVerifyOTP from '../../hooks/useVerifyOTP';
import { Check, X } from 'lucide-react';
import { CustomButton } from '../../utils/Button';
import { toast } from 'react-toastify';

export const VerifyEmailInner = ({ verified, setVerified, email }) => {
  const { control, handleSubmit } = useForm();
  const [timeLeft, setTimeLeft] = useState(86400);

  const navigate = useNavigate();
  const redirectAfterSuccess = true;

  const { otpMutation, isPending } = useVerifyOTP({
    verifyFn: verifyEmail,
    setVerified,
  });

  const { mutate: resendVerifyEmailOTP } = useSendOTP(
    resendVerifyEmailAPI,
    () => setTimeLeft(86400)
  );

  // Convert seconds -> hours, minutes, seconds
  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor((timeLeft % 3600) / 60);
  const seconds = timeLeft % 60;

  // Format to always show 2 digits
  const formatTime = (value) => value.toString().padStart(2, '0');

  const handleContinue = () => {
    if (verified) {
      navigate('/login');
    }
  };

  const onSubmit = (data) => {
    if (timeLeft <= 0) {
      toast.error('OTP has expired. Please request a new one.', {
        autoClose: 3000,
        className:
          'bg-[var(--yellow-primary)] text-black text-sm px-2 py-2 rounded-md',
        closeButton: false,
      });
      return;
    }
    otpMutation({ email, otp: data.otp });
  };

  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-4 mt-6 w-full"
    >
      <OTPInput
        control={control}
        name="otp"
        length={4}
        onAutoSubmit={() => handleSubmit(onSubmit)()}
      />

      {!verified && (
        <div className="text-sm text-gray-600 text-center">
          {timeLeft > 0 ? (
            <>
              OTP expires in:{' '}
              <span className="pl-1">
                {formatTime(hours)}:{formatTime(minutes)}:{formatTime(seconds)}
              </span>
            </>
          ) : (
            <>
              OTP expired
              <button
                type="button"
                className="font-semibold ml-2 text-blue-600 hover:underline"
                onClick={() => resendVerifyEmailOTP({ email })}
              >
                Resend OTP
              </button>
            </>
          )}
        </div>
      )}

      {verified && (
        <div>
          <p className="font-semibold text-[18px] flex items-center gap-2">
            Great, all set!
            <img src="/images/tada.svg" alt="" />
          </p>
          <p>Your account is verified!</p>
        </div>
      )}
      {isPending && <p className="text-[15px]">Code verifying...</p>}
      {verified !== true && verified !== null && (
        <div>
          <p className="font-semibold text-[18px] flex items-center gap-2">
            Oops.. failed, try again
            <img src="/images/no-entry.svg" alt="" />
          </p>
          <p>Wrong otp!</p>
        </div>
      )}
      <hr />
      <div className="w-full flex justify-center">
        <CustomButton
          width="60%"
          className="mt-4"
          text={'Continue'}
          disabled={!verified}
          backgroundColor={verified ? '#FF4A8A' : '#FF4A8A80'}
          textColor="white"
          borderRadius="40px"
          onClick={() => {
            handleContinue();
          }}
        />
      </div>
    </form>
  );
};
